import { Option } from '@common/types/common';
import { Search } from '@components/utility/inputs/Search';
import { InvertedRadio, InvertedRadioChecked } from '@components/utility/radios/InvertedRadio';
import {
    Box,
    BoxProps,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlProps,
    FormHelperText,
    InputLabel,
    InputLabelProps,
    ListItemText,
    ListSubheader,
    Select as MUISelect,
    MenuItem,
    OutlinedInput,
    OutlinedInputProps,
    Radio,
    SelectChangeEvent,
    SelectProps,
    Skeleton,
    TextFieldProps,
    Typography,
} from '@mui/material';
import clsx from 'clsx';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { List, type RowComponentProps } from 'react-window';

const sizes = {
    smallest: 44,
};

type ISelectProps<T extends string | number = string | number> = SelectProps & {
    fcSX?: FormControlProps;
    inputLabelProps?: InputLabelProps;
    items: Option<T>[];
    searchable?: boolean;
    searchProps?: TextFieldProps;
    virtualize?: boolean;
    type?: 'radio' | 'checkbox' | 'default';
    loading?: boolean;
    helperText?: React.ReactNode;
    autoClose?: boolean;
    renderOption?: (item: Option<T>) => ReactElement;
    outlinedInputProps?: OutlinedInputProps;
    placeholder?: string;
    topLabel?: React.ReactNode;
    boxProps?: Omit<BoxProps, 'children'>;
    loadingType?: 'default' | 'skeleton';
};

const ITEM_HEIGHT = 38;
const MAX_HEIGHT = 200; // Max height of dropdown
const LOADING_SPINNER_SIZE = 20;
const LOADING_LABEL_GAP = 8;

const isValueSelected = (selected: unknown, value: string | number) => {
    const selectedValues = Array.isArray(selected) ? selected : [selected];
    return selectedValues.some(
        (selectedValue) =>
            (typeof selectedValue === 'string' || typeof selectedValue === 'number') &&
            String(selectedValue) === String(value)
    );
};

const Select = <T extends string | number = string>({
    items,
    fcSX,
    inputLabelProps,
    searchable,
    searchProps,
    type = 'checkbox',
    loading,
    virtualize,
    helperText,
    autoClose = true, // Even if this is true, multiselects do not autoClose
    renderOption,
    outlinedInputProps,
    topLabel,
    boxProps,
    loadingType = 'default',
    placeholder,
    size,
    ...props
}: ISelectProps<T>) => {
    const [open, setOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const displayedOptions = useMemo(
        () =>
            !searchProps?.onChange
                ? items.filter((option) => option.label.toLowerCase().indexOf(searchText.toLowerCase()) > -1)
                : items,
        [items, searchProps?.onChange, searchText]
    );
    const hasValue = useMemo(() => {
        if (type === 'radio' && !Array.isArray(props.value) && props.value === 'all') {
            return false;
        } else if (
            (Array.isArray(props.value) && !!props.value.length) ||
            (!Array.isArray(props.value) && props.value !== undefined && props.value !== null && props.value !== '')
        ) {
            return true;
        } else {
            return false;
        }
    }, [props.value, type]);

    const renderValue = useCallback(
        (selected: string | number | (string | number)[]) => {
            if (props.renderValue) return props.renderValue(selected);
            else {
                return items
                    .filter((item) => isValueSelected(selected, item.value))
                    .map((item) => item.label)
                    .join(', ');
            }
        },
        [items, props]
    );

    const handleSelectChange = useCallback(
        (value: string | number) => {
            const event = {
                target: {
                    name: props.name,
                    value: value,
                },
            } as SelectChangeEvent<typeof value>;
            if (autoClose) setOpen(false);
            props.onChange?.(event, null);
            setSearchText('');
        },
        [props, autoClose, setOpen]
    );

    const handleMultipleSelectChange = useCallback(
        (value: string | number) => {
            const newValue = Array.isArray(props.value)
                ? props.value.includes(value)
                    ? props.value.filter((v) => v !== value)
                    : [...props.value, value]
                : [value];

            const event = {
                target: {
                    name: props.name,
                    value: newValue,
                },
            } as SelectChangeEvent<typeof newValue>;
            props.onChange?.(event, null);
        },
        [props]
    );

    const renderRow = ({ index, style }: RowComponentProps<object>) => {
        const item = displayedOptions[index];
        return item ? renderItem(item, style) : null;
    };

    const getDropdownHeight = () => {
        const totalItems = displayedOptions.length;
        const totalHeight = totalItems * ITEM_HEIGHT;
        return totalHeight < MAX_HEIGHT ? totalHeight : MAX_HEIGHT;
    };

    const renderItem = useCallback(
        (item: (typeof items)[number], style?: React.CSSProperties) => (
            <MenuItem
                key={'mui-select-item' + item.value}
                value={item.value}
                sx={{
                    padding: '0px 14px',
                    minHeight: 'auto',
                    ...(type !== 'default' && {
                        '&.Mui-selected': {
                            backgroundColor: 'transparent',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        },
                    }),
                }}
                style={style} // Required for react-window to work properly
                onClick={() => {
                    if (Array.isArray(props.value)) {
                        handleMultipleSelectChange(item.value);
                    } else {
                        handleSelectChange(item.value);
                    }
                }}
                selected={isValueSelected(props.value, item.value)}
                disabled={item.disabled}
            >
                <div
                    className={clsx(
                        'flex items-center w-full h-full',
                        { 'py-1': type !== 'checkbox' },
                        { 'border-t-[1px]': items.indexOf(item) }
                    )}
                >
                    {type === 'checkbox' ? <Checkbox checked={isValueSelected(props.value, item.value)} /> : null}
                    {type === 'radio' ? (
                        <Radio
                            checked={isValueSelected(props.value, item.value)}
                            classes={{
                                root: 'p-0 pr-2',
                            }}
                            disableRipple
                            icon={<InvertedRadio />}
                            checkedIcon={<InvertedRadioChecked />}
                        />
                    ) : null}
                    {renderOption ? (
                        renderOption(item)
                    ) : (
                        <ListItemText
                            slotProps={{
                                primary: {
                                    className: '!flex items-center gap-2 !text-sm',
                                },
                            }}
                        >
                            {item.icon ? <item.icon width={16} height={16} /> : null}
                            <Typography>{item.label}</Typography>
                        </ListItemText>
                    )}
                </div>
            </MenuItem>
        ),
        [handleMultipleSelectChange, handleSelectChange, props.value, renderOption, type]
    );

    const SelectComponent = (
        <MUISelect
            {...props}
            input={
                <OutlinedInput
                    notched
                    label={hasValue ? props.label : ''}
                    {...outlinedInputProps}
                    classes={{
                        ...outlinedInputProps?.classes,
                        notchedOutline: clsx(
                            'shadow border-[1px] border-[#D0D5DD]',
                            outlinedInputProps?.classes?.notchedOutline
                        ),
                    }}
                />
            }
            className={clsx('rounded-lg', props.className, {
                'rounded-none rounded-t-lg': open,
                'border-[1px] border-red-500': props.error,
            })}
            {...(autoClose
                ? {
                      open,
                  }
                : {})}
            onOpen={() => setOpen(true)}
            onClose={() => {
                setOpen(false);
                setSearchText('');
                props.onBlur?.({ target: { name: props.name, value: props.value } } as any);
            }}
            renderValue={(selected) => renderValue(selected as string | number | (string | number)[])}
            MenuProps={{
                PaperProps: {
                    sx: {
                        maxHeight: MAX_HEIGHT * 2,
                    },
                    className: 'rounded-lg border-[1px] border-[#D0D5DD] shadow',
                },
                autoFocus: false,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                },
                transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                },
                classes: {
                    list: 'pt-0',
                },
            }}
            disabled={loading ? loading : props.disabled}
            startAdornment={
                loading && loadingType == 'default' ? (
                    <CircularProgress style={{ height: 'auto' }} size={LOADING_SPINNER_SIZE} />
                ) : (
                    props.startAdornment
                )
            }
            size={size === 'smallest' ? 'small' : size}
            onChange={undefined}
            sx={{
                height: size === 'smallest' ? sizes.smallest : undefined,
            }}
        >
            <ListSubheader className="pt-2">
                <InputLabel
                    {...inputLabelProps}
                    shrink={false}
                    sx={{ color: 'black' }}
                    className="mb-3"
                    focused
                    size="normal"
                >
                    {props.label}
                </InputLabel>
                {searchable ? (
                    <Search
                        autoFocus={false}
                        {...searchProps}
                        fullWidth
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            searchProps?.onChange && searchProps.onChange(e);
                        }}
                        onKeyDown={(e) => {
                            if (e.key !== 'Escape') {
                                // Prevents autoselecting item while typing (default Select behaviour)
                                e.stopPropagation();
                            }
                        }}
                        value={searchText}
                        className={clsx('shadow', searchProps?.className)}
                    />
                ) : null}
            </ListSubheader>
            {virtualize ? (
                <List
                    rowComponent={renderRow}
                    rowCount={displayedOptions.length}
                    rowHeight={ITEM_HEIGHT}
                    rowProps={{}}
                    style={{ height: getDropdownHeight(), width: '100%' }}
                />
            ) : (
                displayedOptions.map((item) => renderItem(item))
            )}
        </MUISelect>
    );

    const LoadingWrapper =
        loading && loadingType === 'skeleton' ? (
            <Skeleton className="max-w-none" variant="rounded">
                {SelectComponent}
            </Skeleton>
        ) : (
            SelectComponent
        );

    const Component = (
        <FormControl error={props.error} {...fcSX} fullWidth={props.fullWidth}>
            {!(loading && loadingType === 'skeleton') && (
                <InputLabel
                    shrink={hasValue}
                    focused={false}
                    {...inputLabelProps}
                    sx={{
                        color: (theme) =>
                            props.disabled || (placeholder && !hasValue)
                                ? theme.palette.text.disabled
                                : theme.palette['neutral-900'].main,
                        marginLeft:
                            loading && loadingType === 'default' && !hasValue
                                ? `${LOADING_SPINNER_SIZE + LOADING_LABEL_GAP}px`
                                : undefined,
                    }}
                    size={size === 'medium' || !size ? 'normal' : 'small'}
                    required={props.required && !topLabel}
                >
                    {placeholder && !hasValue ? placeholder : props.label}
                </InputLabel>
            )}
            {LoadingWrapper}
            {helperText ? <FormHelperText disabled={props.disabled}>{helperText}</FormHelperText> : null}
        </FormControl>
    );

    return topLabel ? (
        <Box {...boxProps}>
            <Typography fontFamily="museo-sans" fontSize={16} fontWeight={600} mb="10px" color="neutral-900">
                {topLabel}
                {props.required ? (
                    <Typography component="span" color="signal-warning">
                        {' '}
                        *
                    </Typography>
                ) : null}
            </Typography>
            {Component}
        </Box>
    ) : (
        Component
    );
};

export { Select };
export type { Option };
